import React from "react";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import PlayerFixtures from "./PlayerFixtures";
import PlayerPoints from "./PlayerPoints";

const FETCH_H2H_FIXTURE_COMPARISON = gql`
  query (
    $seasonId: String!
    $gameweek: Int!
    $team1Id: String!
    $team2Id: String!
  ) {
    team1Gameweek: getGameweek(
      seasonId: $seasonId
      gameweek: $gameweek
      teamId: $team1Id
    ) {
      TeamName
      TotalPoints
      Players {
        Selected
        PointsScoredForTeam
        Player {
          WebName
          RecentPoints
          Position
          TeamName
          CurrentFixtures {
            Opponent
            Home
            Difficulty
            Started
            Finished
          }
        }
      }
    }
    team2Gameweek: getGameweek(
      seasonId: $seasonId
      gameweek: $gameweek
      teamId: $team2Id
    ) {
      TeamName
      TotalPoints
      Players {
        Selected
        PointsScoredForTeam
        Player {
          WebName
          RecentPoints
          Position
          TeamName
          CurrentFixtures {
            Opponent
            Home
            Difficulty
            Started
            Finished
          }
        }
      }
    }
  }
`;

function H2HFixtureComparison({ seasonId, gameweek, team1Id, team2Id }) {
  const { loading, data, error } = useQuery(FETCH_H2H_FIXTURE_COMPARISON, {
    variables: { seasonId, gameweek, team1Id, team2Id },
    skip: !seasonId || !gameweek || !team1Id || !team2Id,
  });

  const getTeamBadge = (teamName) => {
    if (!teamName) {
      return null;
    }

    try {
      return require(`../images/team_badges/${teamName}.svg`);
    } catch (e) {
      return null;
    }
  };

  const selectedPlayers = (gameweekData) => {
    if (!gameweekData || !gameweekData.Players) {
      return [];
    }

    return gameweekData.Players.filter((p) => p.Selected)
      .slice()
      .sort(
        (a, b) =>
          a.Player.Position - b.Player.Position ||
          b.Player.RecentPoints - a.Player.RecentPoints
      );
  };

  if (loading) {
    return <p>Loading fixture comparison...</p>;
  }

  if (error || !data || !data.team1Gameweek || !data.team2Gameweek) {
    return <p>Could not load the H2H fixture comparison.</p>;
  }

  const team1Players = selectedPlayers(data.team1Gameweek);
  const team2Players = selectedPlayers(data.team2Gameweek);
  const rowCount = Math.max(team1Players.length, team2Players.length);

  const team1Rows = Array.from({ length: rowCount }, (_, index) =>
    team1Players[index] ? team1Players[index] : null
  );
  const team2Rows = Array.from({ length: rowCount }, (_, index) =>
    team2Players[index] ? team2Players[index] : null
  );

  return (
    <table className="h2h-comparison-table h2h-comparison-table-unified">
      <colgroup>
        <col className="h2h-col-player" />
        <col className="h2h-col-fixture" />
        <col className="h2h-col-rp" />
        <col className="h2h-col-player" />
        <col className="h2h-col-fixture" />
        <col className="h2h-col-rp" />
      </colgroup>
      <thead>
        <tr>
          <th colSpan="2">{data.team1Gameweek.TeamName}</th>
          <th className="h2h-comparison-recent-points">{data.team1Gameweek.TotalPoints}</th>
          <th colSpan="2">{data.team2Gameweek.TeamName}</th>
          <th className="h2h-comparison-recent-points">{data.team2Gameweek.TotalPoints}</th>
        </tr>
      </thead>
      <tbody>
        {team1Rows.map((lp, index) => {
          const leftPlayer = lp;
          const rightPlayer = team2Rows[index];

          return (
            <tr key={index}>
              <td
                className={leftPlayer ? `waiver-divider waiver-position-${leftPlayer.Player.Position}` : ""}
              >
                {leftPlayer && getTeamBadge(leftPlayer.Player.TeamName) && (
                  <img
                    src={getTeamBadge(leftPlayer.Player.TeamName)}
                    alt={leftPlayer.Player.TeamName}
                    className="player-team-badge"
                  />
                )}
                {leftPlayer ? leftPlayer.Player.WebName : ""}
              </td>
              <td>
                {leftPlayer && (
                  <PlayerFixtures
                    fixtures={leftPlayer.Player.CurrentFixtures}
                    variant="bar"
                  />
                )}
              </td>
              <td className={leftPlayer ? `h2h-comparison-recent-points` : ""}>
                {
                    leftPlayer && (
                        <PlayerPoints fixtures={leftPlayer.Player.CurrentFixtures} points={leftPlayer.Player.RecentPoints} />
                    )
                }
              </td>
              <td
                className={rightPlayer ? `waiver-divider waiver-position-${rightPlayer.Player.Position}` : ""}
              >
                {rightPlayer && getTeamBadge(rightPlayer.Player.TeamName) && (
                  <img
                    src={getTeamBadge(rightPlayer.Player.TeamName)}
                    alt={rightPlayer.Player.TeamName}
                    className="player-team-badge"
                  />
                )}
                {rightPlayer ? rightPlayer.Player.WebName : ""}
              </td>
              <td>
                {rightPlayer && (
                  <PlayerFixtures
                    fixtures={rightPlayer.Player.CurrentFixtures}
                    variant="bar"
                  />
                )}
              </td>
              <td className={rightPlayer ? `h2h-comparison-recent-points` : ""}>
                {
                    rightPlayer && (
                        <PlayerPoints fixtures={rightPlayer.Player.CurrentFixtures} points={rightPlayer.Player.RecentPoints} />
                    )
                }
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default H2HFixtureComparison;
